import React, { useEffect, useState } from 'react';
import { useAuth } from '../../context/AuthContext';

interface AuthModalProps {
  onClose: () => void;
  onSuccess?: () => void;
  message?: string;
}

const AuthModal: React.FC<AuthModalProps> = ({ onClose, onSuccess, message }) => {
  const { login } = useAuth();
  const [isStartingFreeTrial, setIsStartingFreeTrial] = useState(false);
  
  // Check if user is starting a free trial
  useEffect(() => {
    const startingTrial = sessionStorage.getItem('starting_free_trial') === 'true';
    setIsStartingFreeTrial(startingTrial);
  }, []);

  const handleLogin = async (provider: 'google' | 'facebook' | 'microsoft') => {
    try {
      await login(provider);
      if (onSuccess) {
        onSuccess();
      } else {
        onClose();
      }
    } catch (error) {
      console.error('Login failed:', error);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-gray-900 rounded-xl p-8 max-w-md w-full mx-4">
        <div className="flex justify-between items-center mb-6">
          <h2 className="text-2xl font-bold text-white">
            {message || (isStartingFreeTrial ? 'Sign In to Start Free Trial' : 'Sign In')}
          </h2>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-white"
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        <div className="space-y-4">
          <button
            onClick={() => handleLogin('google')}
            className="w-full bg-white text-gray-900 py-3 px-4 rounded-lg flex items-center justify-center space-x-2 hover:bg-gray-100 transition-colors"
          >
            <svg className="w-6 h-6" viewBox="0 0 24 24">
              <path
                fill="#4285F4"
                d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
              />
              <path
                fill="#34A853"
                d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
              />
              <path
                fill="#FBBC05"
                d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
              />
              <path
                fill="#EA4335"
                d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
              />
            </svg>
            <span className="font-medium">Continue with Google</span>
          </button>

          <button
            onClick={() => handleLogin('facebook')}
            className="w-full bg-[#1877F2] text-white py-3 px-4 rounded-lg flex items-center justify-center space-x-2 hover:bg-[#166FE5] transition-colors"
          >
            <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 24 24">
              <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/>
            </svg>
            <span className="font-medium">Continue with Facebook</span>
          </button>

          <button
            onClick={() => handleLogin('microsoft')}
            className="w-full bg-[#2F2F2F] text-white py-3 px-4 rounded-lg flex items-center justify-center space-x-2 hover:bg-[#404040] transition-colors"
          >
            <svg className="w-6 h-6" viewBox="0 0 23 23">
              <path fill="#f25022" d="M1 1h10v10H1z"/>
              <path fill="#00a4ef" d="M1 12h10v10H1z"/>
              <path fill="#7fba00" d="M12 1h10v10H12z"/>
              <path fill="#ffb900" d="M12 12h10v10H12z"/>
            </svg>
            <span className="font-medium">Continue with Microsoft</span>
          </button>
        </div>

        <p className="text-gray-400 text-sm text-center mt-6">
          {isStartingFreeTrial 
            ? 'By continuing, you agree to our Terms of Service and Privacy Policy. You will be prompted to set up your subscription after signing in.'
            : 'By continuing, you agree to our Terms of Service and Privacy Policy'
          }
        </p>
      </div>
    </div>
  );
};

export default AuthModal; 